import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  ArrowRightLeft,
  CircleDot,
  Flame,
  Landmark,
  Layers,
  Plus,
  Users,
} from "lucide-react";
import {
  getCreatorBudget,
  getOfferMatchedCash,
  getOfferOpenCash,
  getOwner,
  getRemainingBudget,
  hasOpenSpots,
  parseOutcomeParts,
} from "../../../utils/p2pHelpers";
import { labelForP2PField } from "../../../data/p2pFieldLabels";

/**
 * Exchange Floor — the analytical landing view of the P2P pit. Reads the same
 * offer feed the board uses and presents it like an exchange: total open vs
 * matched liquidity, a fixture depth ladder, market mix, and a live tape of
 * posts and matches. Pure presentation — no mechanism changes.
 */

const fmtCash = (value) => {
  const v = Number(value) || 0;
  if (v >= 100000) return `${Math.round(v / 1000)}k`;
  if (v >= 10000) return `${(v / 1000).toFixed(1)}k`;
  return v.toLocaleString();
};

const relTime = (iso) => {
  if (!iso) return "";
  const delta = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (delta < 60) return "now";
  if (delta < 3600) return `${Math.floor(delta / 60)}m`;
  if (delta < 86400) return `${Math.floor(delta / 3600)}h`;
  return `${Math.floor(delta / 86400)}d`;
};

const PulseTile = ({ icon: Icon, value, label, hint, tone }) => (
  <div className={`pit-floor-stat${tone ? ` pit-floor-stat-${tone}` : ""}`}>
    <span className="pit-floor-stat-icon"><Icon size={12} /></span>
    <strong>{value}</strong>
    <em>{label}</em>
    {hint ? <b>{hint}</b> : null}
  </div>
);

const RungCrest = ({ team }) => {
  const [failed, setFailed] = useState(false);
  return (
    <span className="pit-floor-rung-crest" aria-hidden>
      {team?.logo && !failed ? (
        <img src={team.logo} alt="" onError={() => setFailed(true)} />
      ) : (
        <span>{(team?.name || "?").slice(0, 1).toUpperCase()}</span>
      )}
    </span>
  );
};

const FillDial = ({ rate, matchedBudget, creatorBudget }) => {
  const r = 17;
  const c = 2 * Math.PI * r;
  return (
    <div
      className="pit-floor-stat pit-floor-dial"
      title={`Cash ${fmtCash(matchedBudget)} of ${fmtCash(creatorBudget)} creator budget matched`}
    >
      <svg viewBox="0 0 44 44" aria-hidden>
        <circle cx="22" cy="22" r={r} className="pit-floor-dial-track" />
        <circle
          cx="22"
          cy="22"
          r={r}
          className="pit-floor-dial-fill"
          strokeDasharray={`${(rate / 100) * c} ${c}`}
          transform="rotate(-90 22 22)"
        />
      </svg>
      <strong>{rate}%</strong>
      <em>budget matched</em>
      <b>{fmtCash(matchedBudget)}/{fmtCash(creatorBudget)} Cash</b>
    </div>
  );
};

/** Top fixtures ranked by open liquidity, with matched/open depth bars. */
const DepthLadder = ({ entries, onFocusFixture }) => {
  if (!entries.length) return null;
  const maxCash = Math.max(
    ...entries.map((e) => e.openCash + e.matchedCash),
    1
  );

  return (
    <section className="pit-floor-panel" aria-label="Liquidity ladder">
      <div className="pit-floor-panel-head">
        <Landmark size={13} />
        <span>Liquidity ladder</span>
        <em>where the cash sits — tap to open</em>
      </div>
      <div className="pit-floor-ladder">
        {entries.map((entry) => {
          const homeTeam = entry.fixture?.teams?.home || {};
          const awayTeam = entry.fixture?.teams?.away || {};
          const home = homeTeam.name || "Home";
          const away = awayTeam.name || "Away";
          const total = entry.openCash + entry.matchedCash;
          return (
            <button
              key={entry.fixtureId}
              type="button"
              className="pit-floor-rung"
              onClick={() => onFocusFixture?.(entry)}
            >
              <span className="pit-floor-rung-match">
                <strong className="pit-floor-rung-teams">
                  <RungCrest team={homeTeam} />
                  <span className="pit-floor-rung-names">
                    {home} <i>v</i> {away}
                  </span>
                  <RungCrest team={awayTeam} />
                </strong>
                <em>
                  {entry.leagueName}
                  {entry.isLive ? " · LIVE" : ""}
                  {" · "}
                  {entry.offerCount} offer{entry.offerCount === 1 ? "" : "s"}
                </em>
              </span>
              <span className="pit-floor-rung-depth" aria-hidden>
                <i
                  className="pit-floor-rung-matched"
                  style={{ width: `${(entry.matchedCash / maxCash) * 100}%` }}
                />
                <i
                  className="pit-floor-rung-open"
                  style={{ width: `${(entry.openCash / maxCash) * 100}%` }}
                />
              </span>
              <span className="pit-floor-rung-nums">
                <strong>{fmtCash(entry.openCash)}</strong>
                <em>open</em>
                <b>{fmtCash(total)} total</b>
              </span>
            </button>
          );
        })}
      </div>
      <div className="pit-floor-ladder-legend">
        <span><i className="pit-floor-key-matched" /> matched</span>
        <span><i className="pit-floor-key-open" /> open to take</span>
      </div>
    </section>
  );
};

/** Open cash split across market types. */
const MarketMix = ({ rows }) => {
  if (!rows.length) return null;
  const max = Math.max(...rows.map((r) => r.openCash), 1);
  return (
    <section className="pit-floor-panel" aria-label="Market mix">
      <div className="pit-floor-panel-head">
        <Layers size={13} />
        <span>Market mix</span>
        <em>open cash by market</em>
      </div>
      <div className="pit-floor-mix">
        {rows.map((row) => (
          <div key={row.section} className="pit-floor-mix-row">
            <strong>{labelForP2PField(row.section) || row.section}</strong>
            <span className="pit-floor-mix-track">
              <i style={{ width: `${(row.openCash / max) * 100}%` }} />
            </span>
            <em>
              {fmtCash(row.openCash)} · {row.count}
            </em>
          </div>
        ))}
      </div>
    </section>
  );
};

/** Latest posts and matches across the floor, newest first. */
const FloorTape = ({ events, onSelectOffer }) => {
  if (!events.length) return null;
  return (
    <section className="pit-floor-panel" aria-label="Floor tape">
      <div className="pit-floor-panel-head">
        <ArrowRightLeft size={13} />
        <span>Floor tape</span>
        <em>latest posts &amp; matches</em>
      </div>
      <div className="pit-floor-tape">
        {events.map((ev) => (
          <button
            key={ev.id}
            type="button"
            className="pit-floor-tick"
            onClick={() => onSelectOffer?.(ev.betId)}
          >
            <span className={`pit-floor-tick-kind pit-floor-tick-${ev.type}`}>
              {ev.type === "match" ? "MATCH" : "POST"}
            </span>
            <span className="pit-floor-tick-body">
              <strong>{ev.who}</strong>
              <em>
                {ev.type === "match" ? "took the against side · " : "backs "}
                {ev.label}
              </em>
            </span>
            <span className="pit-floor-tick-cash">{fmtCash(ev.amount)}</span>
            <span className="pit-floor-tick-time">{relTime(ev.at)}</span>
          </button>
        ))}
      </div>
    </section>
  );
};

const P2PFloorOverview = ({
  bets = [],
  boardSections = [],
  onSelectOffer,
  isAuthenticated,
}) => {
  const pulse = useMemo(() => {
    let openCash = 0;
    let matchedCash = 0;
    let openOffers = 0;
    let creatorBudget = 0;
    let matchedBudget = 0;
    let openCapacity = 0;
    const fixtureIds = new Set();
    bets.forEach((bet) => {
      openCash += getOfferOpenCash(bet);
      matchedCash += getOfferMatchedCash(bet);
      if (hasOpenSpots(bet)) openOffers += 1;
      const fid = bet.fixture?._id || bet.fixture?.id;
      if (fid) fixtureIds.add(String(fid));
      if (bet.status === "Active") {
        const budget = getCreatorBudget(bet);
        creatorBudget += budget;
        matchedBudget += Math.max(0, budget - getRemainingBudget(bet));
        openCapacity += getOfferOpenCash(bet);
      }
    });
    return {
      openCash,
      matchedCash,
      openOffers,
      fixtures: fixtureIds.size,
      openCapacity,
      creatorBudget,
      matchedBudget,
      fillRate: creatorBudget
        ? Math.round((matchedBudget / creatorBudget) * 100)
        : 0,
    };
  }, [bets]);

  const ladder = useMemo(() => {
    const entries = [];
    boardSections.forEach((section) => {
      section.fixtures.forEach((entry) => {
        const matchedCash = entry.offers.reduce(
          (sum, o) => sum + getOfferMatchedCash(o),
          0
        );
        if (entry.stats.openCash <= 0 && matchedCash <= 0) return;
        entries.push({
          fixtureId: entry.fixtureId,
          fixture: entry.fixture,
          leagueName: section.leagueName,
          openCash: entry.stats.openCash,
          matchedCash,
          offerCount: entry.stats.offerCount,
          isLive: entry.stats.status === "Live",
          firstOfferId:
            entry.offers.find(hasOpenSpots)?._id || entry.offers[0]?._id,
        });
      });
    });
    return entries
      .sort(
        (a, b) =>
          b.openCash - a.openCash || b.matchedCash - a.matchedCash
      )
      .slice(0, 8);
  }, [boardSections]);

  const marketMix = useMemo(() => {
    const map = new Map();
    bets.forEach((bet) => {
      const section = bet?.agreedOdds?.section || "other";
      const prev = map.get(section) || { section, openCash: 0, count: 0 };
      prev.openCash += getOfferOpenCash(bet);
      prev.count += 1;
      map.set(section, prev);
    });
    return Array.from(map.values())
      .filter((r) => r.openCash > 0)
      .sort((a, b) => b.openCash - a.openCash)
      .slice(0, 7);
  }, [bets]);

  const tape = useMemo(() => {
    const events = [];
    bets.forEach((bet) => {
      const label =
        parseOutcomeParts(bet.agreedOdds, bet.fixture)?.pickLabel ||
        parseOutcomeParts(bet.agreedOdds, bet.fixture)?.fullText ||
        "an outcome";
      if (bet.createdAt) {
        events.push({
          id: `${bet._id}-post`,
          betId: bet._id,
          type: "post",
          who: getOwner(bet).name || "Someone",
          label,
          amount: bet.amount,
          at: bet.createdAt,
        });
      }
      (bet.peopleJoined || []).forEach((p, i) => {
        if (!p.joinedAt) return;
        events.push({
          id: `${bet._id}-join-${i}`,
          betId: bet._id,
          type: "match",
          who: (typeof p.user === "object" ? p.user?.name : null) || "Someone",
          label,
          amount: p.pendingAmount || bet.amount,
          at: p.joinedAt,
        });
      });
      (bet.invites || []).forEach((inv, i) => {
        if (inv.status !== "Accepted" || !inv.acceptedAt) return;
        events.push({
          id: `${bet._id}-inv-${i}`,
          betId: bet._id,
          type: "match",
          who:
            (typeof inv.user === "object" ? inv.user?.name : null) || "Someone",
          label,
          amount: inv.pendingAmount || bet.amount,
          at: inv.acceptedAt,
        });
      });
    });
    return events
      .sort((a, b) => new Date(b.at) - new Date(a.at))
      .slice(0, 9);
  }, [bets]);

  const onFocusFixture = (entry) => {
    if (entry.firstOfferId) onSelectOffer?.(entry.firstOfferId);
  };

  return (
    <section className="pit-detail pit-floor" aria-label="Exchange floor overview">
      <div className="pit-floor-scroll">
        <header className="pit-floor-head">
          <div>
            <p className="pit-idle-eyebrow">BondP2P · Exchange floor</p>
            <h1>Floor overview</h1>
            <p className="pit-floor-sub">
              Live liquidity across every open head-to-head. Pick a rung on the
              ladder, a tick on the tape, or a bet on the board to open it.
            </p>
          </div>
          <Link
            to={isAuthenticated ? "/app/p2p/create" : "/app/login"}
            state={isAuthenticated ? undefined : { from: "/app/p2p/create" }}
            className="pit-idle-cta"
          >
            <Plus size={12} strokeWidth={2.5} />
            Post challenge
          </Link>
        </header>

        <div className="pit-floor-pulse" aria-label="Floor pulse">
          <PulseTile
            icon={Landmark}
            value={fmtCash(pulse.openCash)}
            label="open to take"
            hint="cash"
            tone="open"
          />
          <PulseTile
            icon={Users}
            value={fmtCash(pulse.matchedCash)}
            label="matched"
            hint="cash"
          />
          <PulseTile icon={Activity} value={pulse.openOffers} label="open offers" />
          <PulseTile icon={CircleDot} value={pulse.fixtures} label="fixtures" />
          <FillDial
            rate={pulse.fillRate}
            matchedBudget={pulse.matchedBudget}
            creatorBudget={pulse.creatorBudget}
          />
          <PulseTile
            icon={Flame}
            value={fmtCash(pulse.openCapacity)}
            label="counter Cash open"
            tone={pulse.openCapacity ? "hot" : ""}
          />
        </div>

        <div className="pit-floor-grid">
          <DepthLadder entries={ladder} onFocusFixture={onFocusFixture} />
          <div className="pit-floor-side">
            <MarketMix rows={marketMix} />
            <FloorTape events={tape} onSelectOffer={onSelectOffer} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default P2PFloorOverview;
